import React, { useState, useEffect } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useApp } from "@/context/AppContext";
import { imgUrl } from "@/lib/api";
import { toast } from "sonner";
import { Heart, Minus, Plus, ZoomIn, X, Box, Weight, Ruler } from "lucide-react";

export function ProductModal({ product, open, onClose }) {
  const { t, lang, favorites, toggleFavorite, addToCart } = useApp();
  const [activeImg, setActiveImg] = useState(0);
  const [size, setSize] = useState("");
  const [qty, setQty] = useState(1);
  const [zoom, setZoom] = useState(false);

  useEffect(() => {
    if (product) {
      setActiveImg(0);
      setSize("");
      setQty(1);
      setZoom(false);
    }
  }, [product]);

  if (!product) return null;
  const name = lang === "es" ? product.name_es : product.name_en;
  const desc = lang === "es" ? product.description_es : product.description_en;
  const isFav = favorites.includes(product.id);
  const images = product.images?.length ? product.images : [];

  const handleAdd = () => {
    if (!size) {
      toast.error(t("selectSize"));
      return;
    }
    addToCart(product, size, qty);
    toast.success(`${name} · ${t("size")} ${size} × ${qty}`);
    onClose();
  };

  return (
    <>
      <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
        <DialogContent
          data-testid="product-modal"
          className="max-w-4xl p-0 overflow-hidden max-h-[92vh] overflow-y-auto kiosk-scroll"
        >
          <div className="grid md:grid-cols-2">
            {/* Gallery */}
            <div className="bg-muted p-4 sm:p-6">
              <div
                className="relative aspect-square rounded-xl overflow-hidden bg-card cursor-zoom-in"
                onClick={() => images.length && setZoom(true)}
                data-testid="product-modal-zoom-button"
              >
                {images.length ? (
                  <img src={imgUrl(images[activeImg])} alt={name} className="h-full w-full object-cover" />
                ) : (
                  <div className="h-full w-full flex items-center justify-center text-muted-foreground">
                    <Box className="h-16 w-16" />
                  </div>
                )}
                {images.length > 0 && (
                  <span className="absolute bottom-3 right-3 px-3 py-1.5 rounded-full bg-background/85 text-xs font-semibold flex items-center gap-1.5">
                    <ZoomIn className="h-4 w-4" /> {t("tapToZoom")}
                  </span>
                )}
              </div>
              {images.length > 1 && (
                <div className="mt-3 flex gap-2 overflow-x-auto kiosk-scroll">
                  {images.map((im, i) => (
                    <button
                      key={i}
                      onClick={() => setActiveImg(i)}
                      className={`h-16 w-16 flex-shrink-0 rounded-lg overflow-hidden border-2 ${
                        i === activeImg ? "border-primary" : "border-transparent"
                      }`}
                    >
                      <img src={imgUrl(im)} alt="" className="h-full w-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Details */}
            <div className="p-5 sm:p-6">
              <div className="flex items-start justify-between gap-3">
                <h2 className="font-display font-bold text-2xl leading-tight">{name}</h2>
                <button
                  data-testid="modal-favorite-button"
                  onClick={() => toggleFavorite(product.id)}
                  className={`h-12 w-12 flex-shrink-0 rounded-full flex items-center justify-center border ${
                    isFav ? "bg-red-50 text-red-500 dark:bg-red-950/70 dark:text-red-400" : "text-muted-foreground"
                  }`}
                >
                  <Heart className={`h-6 w-6 ${isFav ? "fill-current" : ""}`} />
                </button>
              </div>

              <div className="mt-2 font-mono font-bold text-3xl text-primary">${product.price.toFixed(2)}</div>
              {desc && <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{desc}</p>}

              {/* sizes */}
              <div className="mt-5">
                <div className="text-sm font-semibold uppercase tracking-wide mb-2">{t("sizes")}</div>
                <div className="flex flex-wrap gap-2">
                  {product.sizes?.map((s) => (
                    <button
                      key={s}
                      data-testid={`modal-size-${s}`}
                      onClick={() => setSize(s)}
                      className={`min-h-[52px] min-w-[52px] px-4 rounded-xl border-2 font-semibold transition-colors ${
                        size === s
                          ? "border-primary bg-primary text-primary-foreground"
                          : "border-border bg-card hover:border-primary"
                      }`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>

              {/* quantity */}
              <div className="mt-5">
                <div className="text-sm font-semibold uppercase tracking-wide mb-2">{t("quantity")}</div>
                <div className="flex items-center gap-3">
                  <button
                    data-testid="modal-qty-minus"
                    onClick={() => setQty((q) => Math.max(1, q - 1))}
                    className="h-14 w-14 rounded-xl border bg-card flex items-center justify-center active:scale-95"
                  >
                    <Minus className="h-6 w-6" />
                  </button>
                  <span data-testid="modal-qty-value" className="font-mono font-bold text-2xl w-12 text-center">
                    {qty}
                  </span>
                  <button
                    data-testid="modal-qty-plus"
                    onClick={() => setQty((q) => q + 1)}
                    className="h-14 w-14 rounded-xl border bg-card flex items-center justify-center active:scale-95"
                  >
                    <Plus className="h-6 w-6" />
                  </button>
                </div>
              </div>

              {/* packaging */}
              <div className="mt-5 rounded-xl border bg-muted/40 p-4">
                <div className="text-sm font-semibold uppercase tracking-wide mb-3">{t("packaging")}</div>
                <div className="grid grid-cols-3 gap-3 text-center">
                  <div>
                    <Box className="h-5 w-5 mx-auto text-primary" />
                    <div className="mt-1 font-mono font-bold">{product.units_per_box}</div>
                    <div className="text-[11px] text-muted-foreground">{t("unitsPerBox")}</div>
                  </div>
                  <div>
                    <Weight className="h-5 w-5 mx-auto text-primary" />
                    <div className="mt-1 font-mono font-bold">{product.weight_kg} kg</div>
                    <div className="text-[11px] text-muted-foreground">{t("weight")}</div>
                  </div>
                  <div>
                    <Ruler className="h-5 w-5 mx-auto text-primary" />
                    <div className="mt-1 font-mono font-bold text-xs">{product.dimensions}</div>
                    <div className="text-[11px] text-muted-foreground">{t("dimensions")}</div>
                  </div>
                </div>
              </div>

              <Button
                data-testid="add-to-order-button"
                onClick={handleAdd}
                className="mt-5 w-full min-h-[56px] rounded-xl text-base font-bold"
              >
                {t("addToOrder")}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Zoom overlay */}
      {zoom && (
        <div
          data-testid="zoom-overlay"
          className="fixed inset-0 z-[60] bg-black/90 flex items-center justify-center p-4"
          onClick={() => setZoom(false)}
        >
          <button className="absolute top-6 right-6 h-12 w-12 rounded-full bg-white/10 text-white flex items-center justify-center">
            <X className="h-6 w-6" />
          </button>
          <img src={imgUrl(images[activeImg])} alt={name} className="max-h-full max-w-full object-contain" />
        </div>
      )}
    </>
  );
}
